import { type User } from '../../models/User'
import { type UserScoreRow } from '../../shared/insights/store/insights'
import { isAdminOrEnterpriseAdmin, isOwner } from '../../common/userUtils'

export const DECLINE_THRESHOLD = 3

export const MIN_MEETINGS_FOR_SCORE = 5

export const SCORE_BENCHMARK = 70

// A score is only meaningful once there's a minimum sample of meetings behind it
export const hasEnoughMeetingsForScore = (meetingCount: number, score: number | null | undefined): boolean =>
  meetingCount >= MIN_MEETINGS_FOR_SCORE && score != null

export interface ScoreRow {
  id: string
  name: string
  email: string
  score: number | null
  delta: number | null
  meetingCount: number
  history: number[]
  // The average of however few scored meetings a user has, for display only. It is deliberately a field
  // of its own rather than a flag on score, so nothing downstream can mistake it for a real score: it is
  // never compared to SCORE_BENCHMARK, never counted in the team average, never used to assign a group.
  provisionalScore: number | null
}

export const buildScoreRows = (users: User[], scorecards: UserScoreRow[]): ScoreRow[] => {
  const scorecardsByUserId = new Map(scorecards.map((row) => [row.user_id, row]))
  const listed = users.filter((user) => Boolean(user.email?.trim()))
  return listed.map((user): ScoreRow => {
    const scorecard = scorecardsByUserId.get(user.id)
    const meetingCount = scorecard?.meetings_current ?? 0
    const hasScore = hasEnoughMeetingsForScore(meetingCount, scorecard?.avg_score_current)
    // Only populated where it is shown: a user short of the threshold who still has a scored meeting
    const provisional = !hasScore && meetingCount > 0 ? (scorecard?.avg_score_current ?? null) : null
    return {
      id: user.id,
      name: user.name ?? '',
      email: user.email ?? '',
      score: hasScore ? (scorecard?.avg_score_current ?? null) : null,
      delta: hasScore ? (scorecard?.avg_score_trend ?? null) : null,
      meetingCount,
      history: hasScore ? (scorecard?.score_history.map((point) => point.score) ?? []) : [],
      provisionalScore: provisional,
    }
  })
}

// A non-owner never sees other administrators in the roster, but still sees themself even if they
// hold that role. Ranking still runs on the full roster (see groupFullRoster below), so this only
// decides what a non-owner is shown, not who counts as a top performer.
export const filterVisibleRoster = (users: User[], viewer: User | null | undefined): User[] => {
  if (isOwner(viewer)) {
    return users
  }
  return users.filter((user) => user.id === viewer?.id || !isAdminOrEnterpriseAdmin(user))
}

export const filterRowsByQuery = (rows: ScoreRow[], query: string): ScoreRow[] => {
  const normalized = query.trim().toLowerCase()
  if (!normalized) {
    return rows
  }
  return rows.filter((row) => row.name.toLowerCase().includes(normalized) || row.email.toLowerCase().includes(normalized))
}

export type RosterGroupKey = 'belowBenchmark' | 'watchZone' | 'topPerformers' | 'onTrack' | 'missingData'

export const ALL_ROSTER_GROUP_KEYS: RosterGroupKey[] = ['belowBenchmark', 'watchZone', 'topPerformers', 'onTrack', 'missingData']

// Which group a row belongs to on its own merits. Top performers is the one group a single row cannot
// claim, because it is decided by ranking the roster; groupFullRoster lifts those rows out of onTrack.
export const rosterGroup = (row: ScoreRow): Exclude<RosterGroupKey, 'topPerformers'> => {
  if (row.score === null) {
    return 'missingData'
  }
  if (row.score < SCORE_BENCHMARK) {
    return 'belowBenchmark'
  }
  // Slipping is what separates the two groups above the benchmark, at any score: a strong score coming
  // down is still worth a look, so there is no ceiling above which a decline stops counting.
  const declining = (row.delta ?? 0) < -DECLINE_THRESHOLD
  if (declining) {
    return 'watchZone'
  }
  return 'onTrack'
}

export const GROUP_ROW_CAP = 3

// People worth learning from, lifted out of onTrack into a section of their own
export const TOP_PERFORMER_CAP = 3

// Being the top of a weak field is not the same as being worth copying, so the score has to clear the
// benchmark on its own. A top score that is sliding is not a model to copy either, so only steady or
// climbing scores qualify. Rows without a score are already excluded, which is what keeps the minimum
// scored meetings threshold honoured.
export const selectTopPerformers = (rows: ScoreRow[]): ScoreRow[] =>
  rows
    .filter((row) => row.score !== null && row.score >= SCORE_BENCHMARK && (row.delta ?? 0) >= 0)
    .sort((a, b) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, TOP_PERFORMER_CAP)

// Ranking must run on the full roster, not whatever subset the caller happens to pass in: a search query
// filters which rows are visible, but it must not change who counts as a top performer or bump someone
// into that group only because the rest of the roster got filtered out of view.
export const groupFullRoster = (rows: ScoreRow[], fullRoster: ScoreRow[] = rows): Record<RosterGroupKey, ScoreRow[]> => {
  const byScoreAscending = (a: ScoreRow, b: ScoreRow) => (a.score ?? 0) - (b.score ?? 0)
  const byDropSizeDescending = (a: ScoreRow, b: ScoreRow) => (a.delta ?? 0) - (b.delta ?? 0)
  // Everyone who qualifies as a top performer scores at or above the benchmark without slipping, so they
  // can only ever be drawn out of onTrack. They belong to one group at a time, so onTrack gives them up.
  const promoted = new Set(selectTopPerformers(fullRoster).map((row) => row.id))
  return {
    belowBenchmark: rows.filter((row) => rosterGroup(row) === 'belowBenchmark').sort(byScoreAscending),
    watchZone: rows.filter((row) => rosterGroup(row) === 'watchZone').sort(byDropSizeDescending),
    topPerformers: rows
      .filter((row) => promoted.has(row.id))
      .sort(byScoreAscending)
      .reverse(),
    onTrack: rows.filter((row) => rosterGroup(row) === 'onTrack' && !promoted.has(row.id)).sort(byScoreAscending),
    missingData: rows.filter((row) => rosterGroup(row) === 'missingData').sort((a, b) => b.meetingCount - a.meetingCount),
  }
}

export const orderGroupedRoster = (
  rows: ScoreRow[],
  visibleGroups: ReadonlySet<RosterGroupKey> = new Set(ALL_ROSTER_GROUP_KEYS),
  // Rows revealed per group; groups missing from the map show the default cap
  visibleCounts: ReadonlyMap<RosterGroupKey, number> = new Map(),
  fullRoster: ScoreRow[] = rows,
): ScoreRow[] => {
  const groups = groupFullRoster(rows, fullRoster)
  const visible = (key: RosterGroupKey) => {
    if (!visibleGroups.has(key)) {
      return []
    }
    return groups[key].slice(0, visibleCounts.get(key) ?? GROUP_ROW_CAP)
  }
  return [...visible('belowBenchmark'), ...visible('watchZone'), ...visible('topPerformers'), ...visible('onTrack'), ...visible('missingData')]
}
